import React, { useState, Fragment, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Toast from '../actions/Toasts';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiDividerLine from '@mui/material/Divider';
import MuiTextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import LockIcon from '@mui/icons-material/Lock';
import apiService from '../../services/apiServices';
import InputController from '../../controllers/InputController';
import SelectController from '../../controllers/SelectController';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import TuneIcon from '@mui/icons-material/Tune';
import GridOnIcon from '@mui/icons-material/GridOn';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { buildRegisterKS } from '../../utils/payrollRegisterKS';
import { grossFromNet, netFromGross, paramsFromRow, extraHoursPay } from '../../utils/salaryCalc';
import { resolveFiscal, lawBrackets, periodStart } from '../../utils/fiscalPolicy';
import CalculateIcon from '@mui/icons-material/Calculate';
import { exportRegisterExcel, downloadRegisterPdf, printRegister } from '../../utils/payrollRegisterExport';
import ButtonGroup from '@mui/material/ButtonGroup';
import MuiDivider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import ExtraHours from './ExtraHours';
import { useParams, useHistory } from 'react-router-dom';
import usePermissions from '../../hooks/usePermissions';
import PERMISSIONS from '../../config/permissions/permissionsConfig';
import { authFetch } from '../../services/apiServices';
import ExportData from './ExportData';
import { jsPDF } from 'jspdf';
import notificationsService from '../../services/notificationsService';
import config from '../../config/config';

const countItems = (arr) => {
  let count = 1;
  arr.forEach((item) => {
    item.no = count;
    count += 1;
  });
  return arr;
};

const reOrderItems = (items) => {
  let count = 1;
  items.forEach((item) => {
    if (item.status !== -1) {
      item.no = count;
      count += 1;
    } else {
      item.no = 0;
    }
  });

  return items;
};

const setEditModeFalse = (items) => items.map((obj) => ({ ...obj, editMode: false }));

const isDeleted = (currentValue) => currentValue.status === -1;

const normalizePercent = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const STATUS = { added: 1, deleted: -1, changed: 2, unchanged: 0 };

const getWorkerId = (worker) => worker?.id ?? worker?.Id ?? worker?.ID ?? worker?.WorkerId ?? 0;
const getWorkerName = (worker) => {
  if (!worker) return '';
  if (worker.name) return worker.name;
  const first = worker.FirstName || '';
  const last = worker.LastName || '';
  return `${first} ${last}`.trim();
};

export default function AddList() {
  const { t } = useTranslation();
  //const classes = useStyles();
  const defaultSalaryRow = {
    WorkerId: 0,
    Advances: 0,
    id: 0,
    Salary: 0,
    ExtraHours: 0,
    ExtraHoursWeekend: 0,
    ExtraHoursHoliday: 0,
    HourSalary: 0,
    Bonus: 0,
    Penalties: 0,
    editMode: false,
    no: 1,
    SalaryDate: '',
    status: 0,
  };
  const toastDefaults = { open: false, message: '', variant: '' };
  const [toast, setToast] = useState(toastDefaults);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [salaries, setSalaries] = useState([{ ...defaultSalaryRow }]);
  const [workers, setWorkers] = useState([]);
  const [workerDirectory, setWorkerDirectory] = useState([]);
  const [months, setMonths] = useState([]);
  const [modal, setModal] = useState([]);
  // fiscalRef — tax brackets for income tax calculation
  const fiscalRef = useRef(null);
  // fiscalPolicyRef — full active policy with overtime rate percentages (Neni 56)
  const fiscalPolicyRef = useRef(null);
  // pendingAutoApply — set to true when addAllWorkers is called, auto-runs prefillFromSchedule once contracts load
  const pendingAutoApply = useRef(false);
  // needsAutoAdd — state flag: true when opening an existing list with 0 rows, triggers addAllWorkers once workers load
  const [needsAutoAdd, setNeedsAutoAdd] = useState(false);
  // prefillFromScheduleRef — stable ref to prefillFromSchedule to avoid TDZ in early useEffect
  const prefillFromScheduleRef = useRef(null);
  // applyMonthAdjustmentsRef — po ai trik për bartjen e rregullimeve, thirrur
  // menjëherë pas mbushjes nga orari, që një listë e re të vijë me shtesat,
  // avancet dhe ndalesat e periudhës brenda.
  const applyMonthAdjustmentsRef = useRef(null);
  const notificationsConfigured = Boolean(config.notificationsApiUrl);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm();
  const { id: routeId } = useParams();
  const listId = Number.isFinite(Number(routeId)) ? Number(routeId) : null;

  /* ── Procesimi i pagave ────────────────────────────────────────────────────
   * "Proceso pagat" e shënon muajin si të procesuar dhe i lidh rregullimet e
   * aprovuara me këtë listë, që të mos hyjnë dy herë.
   *
   * Nuk është bravë: lista mbetet e ndryshueshme edhe pas tij, sepse një gabim
   * i vënë re më vonë duhet vetëm korrigjuar e riprocesuar — jo rihapur nga një
   * faqe tjetër. Gjurma e çdo ndryshimi mbetet te `audit_log`.
   *
   * Ruan së pari, që të mos shënohet një gjendje më e vjetër se ajo në ekran. */
  const { can } = usePermissions();
  const canLock = can(PERMISSIONS.PAYROLL_LOCK) || can(PERMISSIONS.PAYROLL_MANAGE);
  const [listStatus, setListStatus] = useState('open');
  const [lockedBy, setLockedBy]     = useState('');
  const [processOpen, setProcessOpen] = useState(false);
  const [processing, setProcessing]   = useState(false);
  const isClosed = listStatus === 'closed';

  const refreshListStatus = useCallback(async () => {
    if (!listId) return;
    try {
      const r = await authFetch(`/api/salary-lists/${listId}`);
      const j = await r.json().catch(() => ({}));
      if (j?.success && j.data) { setListStatus(j.data.status || 'open'); setLockedBy(j.data.LockedBy || ''); }
    } catch { /* gjendja mbetet 'open' — ekrani nuk bllokohet nga një lexim i dështuar */ }
  }, [listId]);

  useEffect(() => { refreshListStatus(); }, [refreshListStatus]);


  const onRowClick = (no) => {
    const rowItem = salaries.map((item, index) => ({
      ...item,
      editMode: index === no - 1 ? !item.editMode : false,
    }));
    setSalaries(rowItem);
  };

  const addRow = () => {
    const nextNo = Math.max(...salaries.map((o) => o.no), 0) + 1;
    const newItem = { ...defaultSalaryRow, no: nextNo };
    const newState = [...salaries, newItem];
    setSalaries(countItems(newState));
  };

  const deleteRow = (rowId) => {
    const arrayCopy = [...salaries];
    const item = arrayCopy.find((i) => i.no === rowId);
    item.status = -1; //STATUS.deleted;
    setSalaries(countItems(arrayCopy));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const getWorkers = useCallback(() => {
    apiService()
      .get('selectworker')
      .then((response) => {
        if (response.success) {
          setWorkers(response.data);
        } else {
          alert('Error:' + response.errorMessage);
        }
      });
  }, [setWorkers]);

  const getWorkerDirectory = useCallback(() => {
    apiService()
      .get('workers')
      .then((response) => {
        if (response.success) {
          setWorkerDirectory(response.data || []);
        }
      });
  }, [setWorkerDirectory]);

  const setValues = useCallback(
    (items = []) => {
      items.forEach((item) => {
        setValue('WorkerId_' + item.no, workers?.find((w) => getWorkerId(w) === item.WorkerId));
        setValue('Salary_' + item.no, item.Salary);
        setValue('Advances_' + item.no, item.Advances);
        setValue('ExtraHours_' + item.no, item.ExtraHours);
        setValue('HourSalary_' + item.no, item.HourSalary);
        setValue('Bonus_' + item.no, item.Bonus);
        setValue('Penalties_' + item.no, item.Penalties);
        item.status = STATUS.unchanged;
      });
    },
    [setValue, workers]
  );

  const prepareEmployeeCard = useCallback(
    (fiscalRanges, obj = []) => {
      // Pa brez të konfiguruar mbeten ata të ligjit — kurrë listë bosh, që do të
      // thoshte tatim zero për të gjithë.
      const ranges = fiscalRanges && fiscalRanges.length ? fiscalRanges : lawBrackets();

      // Normat e shtesës nga politika fiskale e periudhës (Neni 56)
      const policy = fiscalPolicyRef.current || {};
      // Normat e orëve shtesë nuk lexohen më këtu: pagesën e nxjerr
      // `extraHoursPay`, që i njeh të gjashtë kategoritë e politikës.
      const healthInsEmrPct = normalizePercent(policy?.HealthInsEmployer, 0);

      const employees =
        obj?.map((emp) => {
          const salary = toNumber(emp?.Salary);

          // Pagesa e orëve shtesë — një formulë e vetme (`extraHoursPay`), e
          // përbashkët me dialogun «Orët shtesë». Nëse `HourSalary` është
          // vendosur me dorë, ajo respektohet si shumë totale.
          let overtimePay = toNumber(emp?.HourSalary);
          if (overtimePay === 0 && salary > 0) {
            overtimePay = extraHoursPay(emp, salary, policy).total;
          }

          // Një formulë e vetme për tërë sistemin — kontributet mbi BRUTON,
          // tatimi mbi bruton minus kontributin e punëtorit. Këtu ishte shkruar
          // e dyta kopje e saj, me kontributin mbi pagën bazë.
          //
          // ⚠️ Kjo rrugë kalon edhe për listat e MBYLLURA, me qëllim. `closed`
          // këtu është shenjë se paga u procesua, jo bravë: një gabim i vënë re
          // pas procesimit korrigjohet e riprocesohet në vend (shih
          // `salaryListLock.test.js`). Një variant i kësaj i ndalonte
          // rillogaritjen te listat e mbyllura që ekrani të mos ndryshonte nga
          // ajo që u deklarua — dhe bashkë me të ndaloi EDITIMIN: çdo ndryshim
          // i pagës kalon nga këtu, ndaj rreshti kthehej me shifrat e vjetra
          // dhe fusha dukej sikur nuk punonte fare.
          const row = { ...emp, HourSalary: overtimePay };
          const r   = netFromGross(salary, paramsFromRow(row, policy, ranges));

          const healthInsEmployer = emp?.HealthInsEmployer != null && emp?.HealthInsEmployer !== ''
            ? toNumber(emp.HealthInsEmployer)
            : (healthInsEmrPct / 100) * r.gross;

          return {
            ...emp,
            HourSalary:        overtimePay,
            employeeTaxFee:    r.contribEmployee,
            employerTaxFee:    r.contribEmployer,
            contributions:     r.contribEmployee + r.contribEmployer,
            HealthInsEmployee: r.health,
            HealthInsEmployer: healthInsEmployer,
            HealthInsPersonal: toNumber(emp?.HealthInsPersonal),
            HealthInsFamily:   toNumber(emp?.HealthInsFamily),
            taxes:  r.tax,
            neto:   r.net,
            total:  r.net,
          };
        }) || [];

      setSalaries(employees);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSalaries]
  );

  /** Periudha e listës si `YYYY-MM-01` — sipas saj zgjidhet politika fiskale. */
  const listPeriod = useCallback(() => {
    const monthVal = watch('month');
    const monthId  = monthVal?.id ?? monthVal?.Id ?? (typeof monthVal === 'number' ? monthVal : Number(monthVal));
    return periodStart(Number(watch('year')) || new Date().getFullYear(), monthId);
  }, [watch]);

  /**
   * Politika fiskale dhe brezat e saj — një kërkesë, një rregull, një burim.
   *
   * Më parë ishin dy kërkesa të pavarura: brezat merreshin nga `fiscalPolitics`
   * me **`data[0]`** (rreshti i parë sipas id-së — as aktivja, as ajo e
   * periudhës), kurse normat nga `fiscalPoliciesExt`. Faqja «Politikat Fiskale»
   * shkruan vetëm te e dyta, ndaj shkallët e redaktuara aty NUK arrinin kurrë te
   * llogaritja: ndryshoje tabelën, ruaje, dhe paga dilte njësoj.
   *
   * Për më tepër, `prepareEmployeeCard` mund të fillonte para se të mbërrinin
   * normat — dhe atëherë llogaritej me parazgjedhjet, jo me politikën.
   */
  const getFiscalPolitics = useCallback(
    (obj) => {
      const period = listPeriod();
      Promise.all([
        apiService().get('fiscalPoliciesExt'),
        apiService().get('fiscalPoliticsDetails'),
      ]).then(([pRes, dRes]) => {
        const { policy, ranges } = resolveFiscal({
          policies: pRes?.success ? (pRes.data || []) : [],
          details:  dRes?.success ? (dRes.data || []) : [],
          year:  period ? Number(period.slice(0, 4)) : undefined,
          month: period ? Number(period.slice(5, 7)) : undefined,
        });
        fiscalPolicyRef.current = policy || {};
        fiscalRef.current = ranges;
        prepareEmployeeCard(ranges, obj);
      }).catch(() => prepareEmployeeCard(lawBrackets(period), obj));
    },
    [prepareEmployeeCard, listPeriod]
  );

  const onValueChange = useCallback((no) => {
    setSalaries((prev) => {
      const newArr = [...prev];
      if (!newArr[no - 1]) return prev;
      newArr[no - 1] = {
        ...newArr[no - 1],
        status: STATUS.changed,
        WorkerId: getWorkerId(watch('WorkerId_' + no)),
        Salary: toNumber(watch('Salary_' + no)),
        Advances: toNumber(watch('Advances_' + no)),
        ExtraHours: toNumber(watch('ExtraHours_' + no)),
        HourSalary: toNumber(watch('HourSalary_' + no)),
        Bonus: toNumber(watch('Bonus_' + no)),
        Penalties: toNumber(watch('Penalties_' + no)),
      };
      // Use ref to avoid this callback recreating when fiscal data changes
      if (fiscalRef.current) {
        prepareEmployeeCard(fiscalRef.current, newArr);
      } else {
        getFiscalPolitics(newArr);
      }
      return newArr;
    });
  }, [getFiscalPolitics, prepareEmployeeCard, watch]);

  /**
   * Paga bazë për listën, duke respektuar bazën e dakorduar të punëtorit.
   *
   * Kur punëtori është shënuar "Neto", shuma e kontratës ËSHTË neto — dhe lista
   * e pagave punon me bruto. Pa këtë kthim, "500 neto" do të hynte si 500 bruto
   * dhe punëtori do të merrte rreth 420 në dorë.
   */
  const basePayFor = useCallback((contractSalary, workerProfile, row = {}) => {
    const amount = parseFloat(contractSalary) || 0;
    if (!amount) return 0;
    const basis = (workerProfile?.SalaryType ?? workerProfile?.salary_type) === 'net' ? 'net' : 'gross';
    if (basis !== 'net') return amount;
    const params = paramsFromRow({ ...row, Salary: amount }, fiscalPolicyRef.current || {}, fiscalRef.current || []);
    const solved = grossFromNet(amount, params);
    // Nëse neto e dakorduar është e paarritshme me këto norma, mbetet shuma e
    // kontratës — më mirë një shifër e dukshme se një rresht bosh.
    return solved ? solved.base : amount;
  }, []);

  const getContracts = useCallback(
    (arr = []) => {
      arr.forEach((a) => {
        apiService()
          .query('getWorkerContracts', 'id=' + a.WorkerId)
          .then((wc) => {
            if (wc.success && wc.data.length > 0) {
              const d = wc.data;
              const contract = d[d.length - 1];
              // Use functional update so we always operate on the latest state,
              // and preserve ExtraHours/HourSalary already loaded from the salary list.
              setSalaries((prev) => {
                const newArr = prev.map((row) => {
                  if (row.no !== a.no) return row;
                  return {
                    ...row,
                    Salary:      basePayFor(contract['BaseSalary'], workerDirectory.find((w) => getWorkerId(w) === a.WorkerId), row) || row.Salary || 0,
                    EmployeeTax: parseFloat(contract['EmployeeTax']) || row.EmployeeTax || 0,
                    EmployerTax: parseFloat(contract['EmployerTax']) || row.EmployerTax || 0,
                    // Preserve ExtraHours and HourSalary from the saved salary list
                  };
                });
                if (fiscalRef.current) {
                  prepareEmployeeCard(fiscalRef.current, newArr);
                } else {
                  getFiscalPolitics(newArr);
                }
                setValues(newArr);
                return newArr;
              });
            } else {
              // Worker has no contract yet — leave row as-is (0 salary), user can enter manually
              if (wc.errorMessage) console.warn('getContracts:', wc.errorMessage);
            }
          });
      });
    },
    [getFiscalPolitics, prepareEmployeeCard, setValues]
  );

  const loadContractForRow = useCallback(
    (rowNo, workerId) => {
      if (!workerId) return;
      apiService()
        .query('getWorkerContracts', 'id=' + workerId)
        .then((wc) => {
          if (wc.success && wc.data.length > 0) {
            const contract = wc.data[wc.data.length - 1];
            const workerProfile = workerDirectory.find((w) => getWorkerId(w) === workerId);
            setSalaries((prev) => {
              const updated = prev.map((r) =>
                r.no === rowNo
                  ? {
                      ...r,
                      WorkerId: workerId,
                      Salary: basePayFor(contract['BaseSalary'], workerProfile, r),
                      HourSalary: parseFloat(contract['HourSalary']) || 0,
                      EmployeeTax: parseFloat(contract['EmployeeTax']) || 0,
                      EmployerTax: parseFloat(contract['EmployerTax']) || 0,
                      HealthInsPersonal: +(workerProfile?.HealthInsPersonal || 0),
                      HealthInsFamily:   +(workerProfile?.HealthInsFamily   || 0),
                      status: STATUS.changed,
                    }
                  : r
              );
              setValues(updated);
              // Use ref to avoid callback recreating when fiscal data changes
              if (fiscalRef.current) {
                prepareEmployeeCard(fiscalRef.current, updated);
              } else {
                getFiscalPolitics(updated);
              }
              return updated;
            });
            setValue(`WorkerId_${rowNo}`, workers.find((w) => getWorkerId(w) === workerId) || null);
            setValue(`Salary_${rowNo}`, basePayFor(contract['BaseSalary'], workerProfile, {}));
            setValue(`HourSalary_${rowNo}`, parseFloat(contract['HourSalary']) || 0);
            setValue(`Advances_${rowNo}`, 0);
            setValue(`Bonus_${rowNo}`, 0);
            setValue(`Penalties_${rowNo}`, 0);
            onValueChange(rowNo);
          }
        });
    },
    [getFiscalPolitics, onValueChange, prepareEmployeeCard, setValue, setValues, workers, workerDirectory]
  );

  const addAllWorkers = () => {
    const arr = [];
    workers.forEach((w) => {
      arr.push({ ...defaultSalaryRow, WorkerId: getWorkerId(w) });
    });
    const ordered = reOrderItems(arr);
    setSalaries(ordered);
    setValues(ordered);
    pendingAutoApply.current = true;
    getContracts(ordered);
  };

  // Auto-add all workers when opening an existing list that has no rows
  useEffect(() => {
    if (!needsAutoAdd) return;
    if (!workers.length) return;
    setNeedsAutoAdd(false);
    addAllWorkers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsAutoAdd, workers]);

  const getSalariesByListId = useCallback(
    (list) => {
      const targetId = list?.id ?? list?.Id ?? list?.list_id;
      if (!targetId) return;
      apiService()
        .query('getSalariesByListId', `id=${targetId}`)
        .then((response) => {
          if (response.success) {
            const results = response.data;
            // If existing list has no rows yet, auto-add all workers
            if (results.length === 0) {
              setNeedsAutoAdd(true);
            }
            const orderedResults = reOrderItems(setEditModeFalse(results));
            // Use ref instead of state to avoid this callback recreating when fiscal data loads
            if (fiscalRef.current) {
              prepareEmployeeCard(fiscalRef.current, orderedResults);
            } else {
              setSalaries(orderedResults);
            }
            getContracts(orderedResults);
            setValues(orderedResults);
            if (!fiscalRef.current) {
              getFiscalPolitics(orderedResults);
            }
          }
        });
    },
    [getContracts, getFiscalPolitics, prepareEmployeeCard, setSalaries, setValues]
  );

  const getSalariesMonthlyList = useCallback(
    (listId) => {
      if (!listId) return;
      apiService()
        .query('getSalariesMonthlyList', `id=${listId}`)
        .then((response) => {
          if (response.success && response.data?.length) {
            const rsp = response.data[0];
            getSalariesByListId(rsp);
            setValue('year', rsp.year);
            setValue('month', months.find((m) => m.id === rsp.month));
          }
        });
    },
    [getSalariesByListId, months, setValue]
  );

  function salary(user, no) {
    loadContractForRow(no, getWorkerId(user));
  }

  const handleClose = (value) => {
    setModal({ open: value })
  };

  const openModal = (id, no, item, open) => {
    // Politika ngarkohet një herë te bootstrap-i, sipas periudhës së listës —
    // këtu kishte një ngarkim të dytë me rregull tjetër («aktivja, ose e para»).
    setModal({
      open: open ? open : false,
      tittle: t('salaries.extraHours'),
      id: id,
      no: no,
      item: item,
      fiscalRates: fiscalPolicyRef.current,
      baseSalary: item?.Salary ?? 0,
    });
  };

  const showAddAllWorkers = () => {
    return ((salaries?.length < 2 && salaries[0]?.WorkerId === 0) || salaries?.every(isDeleted));
  };

  // ── prefillFromSchedule ───────────────────────────────────────────────────
  // Calls getPayrollFromSchedule for the selected month/year, then merges
  // OvertimeHours → ExtraHours, WeekendHours → ExtraHoursWeekend,
  // HolidayHours → ExtraHoursHoliday per worker row.
  // RegularHours is shown in the tooltip but does NOT overwrite the contract
  // salary (base pay stays the source of truth).
  const [prefilling, setPrefilling] = useState(false);
  const prefillFromSchedule = useCallback(() => {
    const monthVal = watch('month');
    const yearVal  = watch('year');
    const monthId  = monthVal?.id ?? monthVal?.Id ?? (typeof monthVal === 'number' ? monthVal : Number(monthVal));
    const year     = Number(yearVal) || new Date().getFullYear();
    if (!monthId) {
      setToast({ open: true, message: t('addList.selectMonthFirst', 'Zgjidh muajin para mbushjes automatike'), variant: 'warning' });
      return;
    }
    const activeSalaries = salaries.filter(s => s.WorkerId && s.status !== -1);
    if (!activeSalaries.length) {
      setToast({ open: true, message: t('addList.noRowsToFill', 'Shto punëtorë para mbushjes automatike'), variant: 'warning' });
      return;
    }
    const workerIds = activeSalaries.map(s => s.WorkerId).join(',');
    setPrefilling(true);
    apiService()
      .query('getPayrollFromSchedule', `month=${monthId}&year=${year}&workerIds=${workerIds}`)
      .then((r) => {
        if (!r.success) {
          setToast({ open: true, message: r.errorMessage || t('common.errorOccurred'), variant: 'error' });
          return;
        }
        const byWorker = {};
        (r.data || []).forEach(d => { byWorker[d.WorkerId] = d; });
        setSalaries(prev => {
          const updated = prev.map(row => {
            if (!row.WorkerId || row.status === -1) return row;
            const d = byWorker[row.WorkerId];
            if (!d) return row;
            return {
              ...row,
              ExtraHours:              d.OvertimeHours,
              ExtraHoursWeekend:       d.WeekendHours,
              ExtraHoursHoliday:       d.HolidayHours,
              ExtraHoursNight:         d.NightHours,
              ExtraHoursHolidayWorked: d.HolidayWorkedHours,
              // Keep HourSalary = 0 so prepareEmployeeCard recalculates from rates
              HourSalary: 0,
              status: STATUS.changed,
              _scheduleBreakdown: d.Breakdown,
            };
          });
          if (fiscalRef.current) {
            prepareEmployeeCard(fiscalRef.current, updated);
          } else {
            getFiscalPolitics(updated);
          }
          return updated;
        });
        // Orët e punuara nuk fshihen kurrë; tejkalimet vetëm sinjalizohen —
        // kufiri javor (LP Neni 55) dhe orët shtesë mbi ato të aprovuara janë
        // çështje pajtueshmërie, jo llogaritjeje.
        const rows = r.data || [];
        const overCap  = rows.filter((d) => (d.OverCapWeeks || []).length);
        const overAppr = rows.filter((d) => d.OvertimeOverApproved);
        const unpaid   = rows.filter((d) => (d.UnpaidLeaveHours || 0) > 0);
        // Working Types (PROMPT 12): orë që NUK hyjnë te paga sipas fjalorit —
        // të përjashtuara (tip i papaguar), të grumbulluara (bankë orësh) — dhe
        // pauzat e paguara që u mbajtën. Thuhen me shifër, që askush të mos
        // kërkojë orët «që mungojnë» te fletëpagesa.
        const excluded = rows.filter((d) => (d.ExcludedHours || 0) > 0);
        const accrued  = rows.filter((d) => (d.AccruedHours || 0) > 0);
        const paidBrk  = rows.filter((d) => (d.PaidBreakHoursKept || 0) > 0);
        if (overCap.length || overAppr.length || unpaid.length || excluded.length || accrued.length || paidBrk.length) {
          const parts = [];
          if (excluded.length) parts.push(`${excluded.length} punëtorë me ${excluded.reduce((a, d) => a + d.ExcludedHours, 0).toFixed(1)} orë të papaguara sipas llojit të punës (nuk hyjnë te paga)`);
          if (accrued.length) parts.push(`${accrued.length} punëtorë me ${accrued.reduce((a, d) => a + d.AccruedHours, 0).toFixed(1)} orë të grumbulluara (bankë orësh, nuk paguhen tani)`);
          if (paidBrk.length) parts.push(`${paidBrk.length} punëtorë: ${paidBrk.reduce((a, d) => a + d.PaidBreakHoursKept, 0).toFixed(1)} orë pauzash të paguara u mbajtën në pagë`);
          if (overCap.length) parts.push(t('addList.overCapWeeks', { count: overCap.length, defaultValue: `${overCap.length} punëtorë kalojnë kufirin javor të orëve shtesë` }));
          if (overAppr.length) parts.push(t('addList.overApproved', { count: overAppr.length, defaultValue: `${overAppr.length} punëtorë kanë më shumë orë se ato të aprovuara` }));
          // Pushimi pa pagë nuk zbritet vetvetiu nga paga mujore — vendimi si
          // zbritet (ditë apo orë) i takon politikës së kompanisë, ndaj këtu
          // vetëm sinjalizohet me shifrën e saktë.
          if (unpaid.length) parts.push(t('addList.unpaidLeave', { count: unpaid.length, hours: unpaid.reduce((a, d) => a + d.UnpaidLeaveHours, 0).toFixed(1), defaultValue: `${unpaid.length} punëtorë me pushim pa pagë` }));
          setToast({ open: true, message: parts.join(' · '), variant: 'warning' });
        } else {
          setToast({ open: true, message: t('addList.scheduleApplied', 'Orari u aplikua me sukses'), variant: 'success' });
        }
      })
      .finally(() => setPrefilling(false));
  }, [getFiscalPolitics, prepareEmployeeCard, salaries, setToast, t, watch]);

  // ── applyMonthAdjustments ─────────────────────────────────────────────────
  // Një thirrje e vetme për të katër kategoritë. Më parë ishin dy funksione
  // pothuajse identike (bonuset, avancet) dhe ndalesat s'kishin fare rrugë.
  //
  // Shumat VENDOSEN, nuk mblidhen: shtypja e butonit dy herë jep të njëjtin
  // rezultat si një. Shënimi 'procesuar' nuk bëhet këtu por te "Proceso pagat",
  // sepse deri atëherë shifrat te ekrani janë ende një draft i paruajtur.
  const [applyingAdj, setApplyingAdj] = useState(false);
  const applyMonthAdjustments = useCallback((silent = false) => {
    const monthVal = watch('month');
    const yearVal  = watch('year');
    const monthId  = monthVal?.id ?? monthVal?.Id ?? (typeof monthVal === 'number' ? monthVal : Number(monthVal));
    const year     = Number(yearVal) || new Date().getFullYear();
    if (!monthId) {
      if (!silent) setToast({ open: true, message: t('addList.selectMonthFirst', 'Zgjidh muajin para mbushjes automatike'), variant: 'warning' });
      return;
    }
    const activeSalaries = salaries.filter(s => s.WorkerId && s.status !== -1);
    if (!activeSalaries.length) {
      if (!silent) setToast({ open: true, message: t('addList.noRowsToFill', 'Shto punëtorë para mbushjes automatike'), variant: 'warning' });
      return;
    }
    const workerIds = activeSalaries.map(s => s.WorkerId).join(',');
    setApplyingAdj(true);
    apiService()
      .query('getMonthAdjustments', `month=${monthId}&year=${year}&workerIds=${workerIds}`)
      .then((r) => {
        if (!r.success) {
          if (!silent) setToast({ open: true, message: r.errorMessage || t('common.errorOccurred'), variant: 'error' });
          return;
        }
        const byWorker = {};
        (r.data || []).forEach(d => { byWorker[d.WorkerId] = d; });
        const matched = Object.keys(byWorker).length;
        if (!matched) {
          if (!silent) setToast({ open: true, message: t('addList.noAdjustmentsForMonth', 'Nuk ka rregullime të aprovuara për këtë periudhë'), variant: 'info' });
          return;
        }
        setSalaries(prev => {
          const updated = prev.map(row => {
            if (!row.WorkerId || row.status === -1) return row;
            const d = byWorker[row.WorkerId];
            if (!d) return row;
            const next = {
              ...row,
              Bonus:     Number(d.Bonus)     || 0,
              Advances:  Number(d.Advances)  || 0,
              Penalties: Number(d.Penalties) || 0,
            };
            const same = Number(row.Bonus || 0) === next.Bonus
              && Number(row.Advances || 0) === next.Advances
              && Number(row.Penalties || 0) === next.Penalties;
            return same ? row : { ...next, status: STATUS.changed };
          });
          // Këto janë inpute të regjistruara dhe onValueChange i rilexon nga
          // forma — pa sinkronizim, gjendja do të mbante shumat e reja por
          // rreshti do të ruante përsëri ato të vjetrat.
          updated.forEach(row => {
            if (!row.WorkerId || row.status === -1) return;
            setValue(`Bonus_${row.no}`,     Number(row.Bonus)     || 0);
            setValue(`Advances_${row.no}`,  Number(row.Advances)  || 0);
            setValue(`Penalties_${row.no}`, Number(row.Penalties) || 0);
          });
          if (fiscalRef.current) {
            prepareEmployeeCard(fiscalRef.current, updated);
          } else {
            getFiscalPolitics(updated);
          }
          return updated;
        });
        setToast({
          open: true,
          message: t('addList.adjustmentsApplied', `U bartën rregullimet e aprovuara për ${matched} punonjës`),
          variant: 'success',
        });
      })
      .catch((e) => {
        if (!silent) setToast({ open: true, message: e.message || t('common.errorOccurred'), variant: 'error' });
      })
      .finally(() => setApplyingAdj(false));
  }, [getFiscalPolitics, prepareEmployeeCard, salaries, setToast, setValue, t, watch]);


  // Keep ref in sync so the auto-apply useEffect below can call it safely
  prefillFromScheduleRef.current = prefillFromSchedule;
  applyMonthAdjustmentsRef.current = applyMonthAdjustments;

  // Auto-apply schedule once all contracts have loaded after addAllWorkers
  useEffect(() => {
    if (!pendingAutoApply.current) return;
    const active = salaries.filter((s) => s.WorkerId && s.status !== -1);
    if (!active.length) return;
    const allLoaded = active.every((s) => s.Salary > 0);
    if (!allLoaded) return;
    pendingAutoApply.current = false;
    prefillFromScheduleRef.current?.();
    // silent=true: një listë e re pa bonuse për atë muaj s'ka pse të nxjerrë
    // paralajmërim — përdoruesi s'e kërkoi shprehimisht këtë hap.
    applyMonthAdjustmentsRef.current?.(true);
  }, [salaries]);

  useEffect(() => {
    apiService()
      .get('months')
      .then((response) => {
        if (response.success) {
          const normalized = response.data.map((m) => ({ id: m.Id, name: m.Name }));
          setMonths(normalized);
          // Auto-select current month only when creating a new list
          if (!listId) {
            const curMonth = new Date().getMonth() + 1;
            const mo = normalized.find((m) => m.id === curMonth);
            if (mo) setValue('month', mo);
          }
        } else {
          alert('Error:' + response.errorMessage);
        }
      });

    const date = new Date();
    setValue('year', date.getFullYear());
    getWorkers();
    getWorkerDirectory();
  }, [getWorkerDirectory, getWorkers, listId, setValue]);

  // Bootstrap: politika e periudhës dhe brezat e saj, pa prekur gjendjen e pagave.
  // (I njëjti rregull si te `getFiscalPolitics` — ky ishte vendi i TRETË ku
  // merrej `data[0]` i `fiscalPolitics`, pra politika e parë sipas id-së.)
  useEffect(() => {
    const period = listPeriod();
    Promise.all([
      apiService().get('fiscalPoliciesExt'),
      apiService().get('fiscalPoliticsDetails'),
    ]).then(([pRes, dRes]) => {
      const { policy, ranges } = resolveFiscal({
        policies: pRes?.success ? (pRes.data || []) : [],
        details:  dRes?.success ? (dRes.data || []) : [],
        year:  period ? Number(period.slice(0, 4)) : undefined,
        month: period ? Number(period.slice(5, 7)) : undefined,
      });
      fiscalPolicyRef.current = policy || {};
      fiscalRef.current = ranges;
    }).catch(() => { fiscalRef.current = lawBrackets(period); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listPeriod]);

  useEffect(() => {
    if (listId) {
      getSalariesMonthlyList(listId);
    }
  }, [getSalariesMonthlyList, listId, workers]);

  const onSubmit = (data) => {
    const monthId = (data?.month?.id ?? data?.month?.Id ?? (typeof data?.month === 'number' ? data.month : Number(data?.month))) || null;
    if (!monthId) {
      setToast({ open: true, message: t('addList.selectMonthFirst', 'Zgjidh muajin para ruajtjes'), variant: 'warning' });
      return;
    }
    const year = data?.year || new Date().getFullYear();
    const monthName = months.find((m) => String(m.id) === String(monthId))?.name || `Muaji ${monthId}`;
    const payload = {
      month: monthId,
      year,
      // salary_lists.name is NOT NULL — always send a human-readable label.
      name: `${monthName} ${year}`,
      createdBy: 'Admin',
    };
    if (listId) {
      payload.id = listId;
    }

    // Kthehen premtimet që `handleSubmit(onSubmit)()` të zgjidhet pasi ruajtja
    // të ketë përfunduar vërtet — jo sapo të jetë nisur.
    return listId ? saveSalariesMonthlyList(payload) : checkList(payload);
  };

  const checkList = (data) => {
    const month = (data?.month?.id ?? data?.month?.Id ?? Number(data?.month)) || 0;
    const year  = Number(data?.year) || new Date().getFullYear();
    if (!month) {
      setToast({ open: true, message: t('addList.selectMonthFirst', 'Zgjidh muajin para ruajtjes'), variant: 'warning' });
      return;
    }
    apiService().query('checkList', `month=${month}&year=${year}`)
      .then((response) => {
        if (response.success && response.data.length > 0) {
          const existingId = response.data[0]?.id ?? response.data[0]?.Id;
          setToast({
            open: true,
            message: existingId
              ? `Lista për këtë periudhë ekziston tashmë! (ID: ${existingId})`
              : 'Lista për këtë periudhë ekziston tashmë!',
            variant: 'warning',
          });
          if (existingId) {
            setTimeout(() => {
              window.location.href = `#/salarieslist/editlist/${existingId}`;
            }, 1500);
          }
        }
        else {
          saveSalariesMonthlyList(data);
        }
      });
  }

  /** Ruaj gjendjen aktuale, pastaj mbyll listën. Serveri e refuzon një listë bosh. */
  const processPayroll = async () => {
    setProcessing(true);
    try {
      // Ruajtja së pari: mbyllja e një gjendjeje më të vjetër se ekrani do të
      // ngrinte shifra që përdoruesi s'i pa kurrë.
      await handleSubmit(onSubmit)();
      const r = await authFetch(`/api/salary-lists/${listId}/lock`, { method: 'PUT' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) {
        setToast({ open: true, variant: 'error', message: j.error || 'Pagat nuk u procesuan.' });
        return;
      }
      setListStatus(j.data?.status || 'closed');
      setLockedBy(j.data?.LockedBy || '');
      setProcessOpen(false);

      // Rregullimet shënohen si të procesuara VETËM tani, jo kur u bartën te
      // ekrani: deri këtu shifrat ishin draft. Pas kësaj serveri i përjashton
      // nga bartjet e ardhshme (`WHERE status='approved'`) dhe i mbyll për
      // ndryshim — pra e njëjta shtesë nuk hyn dot dy herë në pagë.
      // Pa `ids`: serveri i gjen vetë rregullimet e aprovuara të periudhës së
      // kësaj liste. Më parë id-të vinin nga një ref lokal, i mbushur vetëm kur
      // bartja ekzekutohej në atë seancë — pra hapja e një liste të ruajtur dhe
      // procesimi nuk shënonte asgjë, në heshtje.
      let adjMsg = '';
      try {
        const pr = await authFetch('/api/payroll-adjustments/process', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ salaryListId: listId }),
        });
        const pj = await pr.json().catch(() => ({}));
        if (pr.ok && pj.success !== false) {
          if (pj.data?.processed) adjMsg = ` ${pj.data.processed} rregullime u lidhën me këtë listë.`;
        } else {
          adjMsg = ' Paralajmërim: rregullimet nuk u shënuan si të procesuara.';
        }
      } catch {
        adjMsg = ' Paralajmërim: rregullimet nuk u shënuan si të procesuara.';
      }
      // "e mbyllur" nuk vlen më si fjalë: pas vendimit që redaktimi lejohet edhe
      // pas procesimit, statusi `closed` është shenjë se paga u procesua, jo
      // bravë. Teksti i vjetër i thoshte përdoruesit të kundërtën e asaj që
      // ekrani lejon.
      setToast({ open: true, variant: 'success', message: `Pagat u procesuan.${adjMsg}` });
    } catch {
      setToast({ open: true, variant: 'error', message: 'Nuk u lidh dot me serverin.' });
    } finally {
      setProcessing(false);
    }
  };

  const saveSalariesMonthlyList = (data) => {
    if (listId) {
      data.id = listId;
      return apiService()
        .put('salariesmonthlylists', data)
        .then((response) => {
          if (response.success) {
            return saveSalaries({ id: listId, month: watch('month').id });
          } else {
            setToast({
              open: true,
              message: t('common.errorOccurred'),
              variant: 'error',
            });
          }
        });
    }
    else {
      apiService()
        .post('salariesmonthlylists', data)
        .then((response) => {
          if (response.success) {
            const inserted =
              Array.isArray(response.data) && response.data.length
                ? response.data[0]
                : response.data;
            const createdListId = inserted?.id ?? inserted?.Id;
            const listMonth = inserted?.month ?? inserted?.Month ?? data?.month;
            if (createdListId) {
              saveSalaries({ id: createdListId, month: listMonth });
            } else {
              setToast({
                open: true,
                message: t('addList.listCreatedMissingId'),
                variant: 'warning',
              });
            }
          } else {
            setToast({
              open: true,
              message: t('common.errorOccurred'),
              variant: 'error',
            });
          }
        });
    }
  }

  const sanitizeSalaryEntity = (item = {}) => {
    // Mbaj vetëm fushat që ekzistojnë në tabelën worker_salaries
    const allowed = [
      'id',
      'Id',
      'listId',
      'ListId',
      'month',
      'Month',
      'WorkerId',
      'workerId',
      'SalaryDate',
      'Salary',
      'BaseSalary',
      'Advances',
      'ExtraHours',
      'ExtraHoursWeekend',
      'ExtraHoursHoliday',
      'HourSalary',
      'Bonus',
      'Penalties',
      // Fusha të llogarituara — nevojiten për raportet (creditor, EmployeeCard, etj.)
      'neto',
      'taxes',
      'contributions',
      'employeeTaxFee',
      'employerTaxFee',
      'HealthInsEmployee',
      'HealthInsEmployer',
    ];
    const clean = {};
    allowed.forEach((key) => {
      if (item[key] !== undefined) clean[key] = item[key];
    });

    // Normalizim vlerash numerike dhe datash për DB
    clean.Salary = toNumber(clean.Salary);
    clean.Advances = toNumber(clean.Advances);
    clean.ExtraHours = toNumber(clean.ExtraHours);
    clean.ExtraHoursWeekend = toNumber(clean.ExtraHoursWeekend);
    clean.ExtraHoursHoliday = toNumber(clean.ExtraHoursHoliday);
    clean.HourSalary = toNumber(clean.HourSalary);
    clean.Bonus = toNumber(clean.Bonus);
    clean.Penalties = toNumber(clean.Penalties);
    clean.SalaryDate = clean.SalaryDate ? clean.SalaryDate : null;
    return clean;
  };

  const saveSalaries = (list) => {
    const listId = list?.id ?? list?.Id;
    if (!listId) {
      setToast({
        open: true,
        message: t('addList.missingListId'),
        variant: 'error',
      });
      return;
    }

    let objs = [], items = reOrderItems([...salaries]), objE = {};

    items?.forEach(item => {
      const baseEntity = sanitizeSalaryEntity({
        ...item,
        listId: listId,
        month: list.month,
      });

      if (item.id === 0) {
        objE = {};
        objE.id = 0;
        objE.entity = baseEntity;
        objE.status = STATUS.added;
        objE.type = "salaries";

        if (item.WorkerId !== 0 && item.no !== 0 && item.status !== -1) {
          objs.push(objE);
        }
      }
      else {
        objE = {};
        objE.entity = baseEntity;
        objE.status = item.status ? item.status : STATUS.unchanged;
        objE.type = "salaries";
        if (objE.status !== STATUS.unchanged) {
          objs.push(objE)
        }
      }
    });

    if (objs.length > 0) {
      // Llogarit pagat në backend para ruajtjes — "llogaritjet bëhen në backend"
      const inputRows = objs
        .filter(o => o.status !== STATUS.deleted)
        .map(o => o.entity);

      // Kthen premtim: pa këtë, "Proceso pagat" e mbyll listën para se rreshtat
      // të kenë mbërritur te serveri, dhe rojtari i sheh 0 rreshta → 400.
      const doSave = (computedEntities) => {
        let finalObjs = objs;
        if (computedEntities && computedEntities.length) {
          let ci = 0;
          finalObjs = objs.map(o => {
            if (o.status === STATUS.deleted) return o;
            const computed = computedEntities[ci++];
            if (!computed) return o;
            return { ...o, entity: { ...o.entity, ...computed } };
          });
        }
        return apiService().saveChanges(finalObjs)
          .then((response) => {
            if (response.success) {
              if (fiscalRef.current) {
                prepareEmployeeCard(fiscalRef.current, salaries);
              }
              setToast({
                open: true,
                message: 'U ruajten me sukses!',
                variant: 'success',
              });
              setSalaries(setEditModeFalse(salaries));
              notifyEmployeesAboutSalaries(list, finalObjs);
              if (!listId) {
                const redirectId = list?.id ?? list?.Id;
                if (redirectId) {
                  window.location.href = '#/salarieslist/editlist/' + redirectId;
                }
              }
            }
            else {
              // Serveri e kthen shkakun për çdo rresht (`data[i].error`); pa e
              // shfaqur, një dështim skeme si "column ... does not exist" dukej
              // si një gabim gjenerik dhe pagat rrinin pa u ruajtur pa u kuptuar.
              const first = (response?.data || []).find((r) => r?.success === false)?.error;
              setToast({
                open: true,
                message: first ? `Ruajtja dështoi: ${first}` : t('common.errorOccurred'),
                variant: 'error',
              });
            }
          });
      };

      if (inputRows.length > 0) {
        return apiService()
          .execute('computeSalary', { rows: inputRows })
          .then(r => doSave(r.success && r.data?.length ? r.data : null))
          .catch(() => doSave(null));
      } else {
        return doSave(null);
      }
    }
    else {
      // routeId is null for /addlist, set for /editlist/:id
      const isNewList = !Number.isFinite(Number(routeId)) || !Number(routeId);
      if (isNewList && listId) {
        // List was just created but has no workers yet — redirect to edit page
        setToast({ open: true, message: 'Lista u krijua! Tash mund të shtosh punëtorët.', variant: 'success' });
        setTimeout(() => { window.location.href = `#/salarieslist/editlist/${listId}`; }, 900);
      } else if (listId) {
        setToast({
          open: true,
          message: 'Nuk ka ndryshime!',
          variant: 'info',
        });
      }
      else {
        setToast({
          open: true,
          message: 'U ruajten me sukses!',
          variant: 'success',
        });
      }
    }
  }

  const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatCurrency = (value) => {
    const amount = toNumber(value);
    return new Intl.NumberFormat('sq-AL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  /** E njëjta formulë si te ndërtimi i rreshtave — `salaryCalc`, jo një kopje e tretë. */
  const computeFiscalTotals = (item) => {
    const ranges =
      (fiscalRef.current && fiscalRef.current.length && fiscalRef.current) ||
      lawBrackets(listPeriod());
    const policy = fiscalPolicyRef.current || {};
    const r = netFromGross(toNumber(item?.Salary), paramsFromRow(item, policy, ranges));
    const healthInsEmrPct = normalizePercent(policy?.HealthInsEmployer, 0);
    const healthInsEmployer = item?.HealthInsEmployer !== undefined
      ? toNumber(item.HealthInsEmployer)
      : (healthInsEmrPct / 100) * r.gross;

    return {
      employeeContribution: r.contribEmployee,
      employerContribution: r.contribEmployer,
      totalContribution:    r.contribEmployee + r.contribEmployer,
      healthInsEmployee:    r.health,
      healthInsEmployer,
      personalHealthIns: toNumber(item?.HealthInsPersonal),
      familyHealthIns:   toNumber(item?.HealthInsFamily),
      taxes:         r.tax,
      netPay:        r.net,
      grossIncome:   r.gross,
      taxableIncome: r.taxable,
    };
  };

  const sanitizeFileToken = (value) =>
    String(value || '')
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60)
      .toLowerCase() || 'punetor';

  const buildSalaryPdfFileName = (name, monthName, year) => {
    const workerToken = sanitizeFileToken(name || 'punetor');
    const monthToken = sanitizeFileToken(monthName || 'muaji');
    const yearToken = year ? `-${year}` : '';
    return `Paga-${workerToken}-${monthToken}${yearToken}.pdf`;
  };

  const buildSalaryTotals = (item) => {
    const baseSalary = toNumber(item?.Salary);
    const overtimePay = toNumber(item?.HourSalary);
    const bonus = toNumber(item?.Bonus);
    const advances = toNumber(item?.Advances);
    const penalties = toNumber(item?.Penalties);
    const fiscal = computeFiscalTotals(item);
    const taxes = fiscal.taxes ?? toNumber(item?.taxes ?? item?.Taxes);
    const employeeContribution =
      fiscal.employeeContribution ??
      toNumber(item?.contributions ?? item?.Contributions ?? item?.employeeTaxFee ?? item?.EmployeeTaxFee);
    const employerContribution = fiscal.employerContribution ?? toNumber(item?.employerTaxFee ?? item?.EmployerTaxFee);
    const combinedContributions =
      fiscal.totalContribution ??
      employeeContribution + employerContribution;
    const storedNet = toNumber(item?.neto);
    const storedTotal = toNumber(item?.total);
    const grossIncome = fiscal.grossIncome ?? baseSalary + overtimePay + bonus;
    const computedNet = fiscal.netPay ?? grossIncome - employeeContribution - taxes - advances - penalties;
    const netPay = storedTotal || storedNet || computedNet;
    const totalHours =
      toNumber(item?.ExtraHours) + toNumber(item?.ExtraHoursWeekend) + toNumber(item?.ExtraHoursHoliday);
    const personalHealthIns = fiscal.personalHealthIns ?? toNumber(item?.HealthInsPersonal);
    const familyHealthIns   = fiscal.familyHealthIns   ?? toNumber(item?.HealthInsFamily);
    const totalDeductions = employeeContribution + taxes + advances + penalties + personalHealthIns + familyHealthIns;

    return {
      baseSalary,
      overtimePay,
      totalHours,
      bonus,
      advances,
      penalties,
      taxes,
      contributions: employeeContribution,
      employerContribution,
      combinedContributions,
      personalHealthIns,
      familyHealthIns,
      netPay,
      grossIncome,
      totalDeductions,
    };
  };

  const payrollMonthNames = ['janar', 'shkurt', 'mars', 'prill', 'maj', 'qershor', 'korrik', 'gusht', 'shtator', 'tetor', 'nëntor', 'dhjetor'];

  const formatPeriodDate = (date) => {
    if (!date) return '—';
    const month = payrollMonthNames[date.getMonth()] || '';
    return `${date.getDate()} ${month} ${date.getFullYear()}`.trim();
  };

  const buildPayrollPeriod = (monthId, year, referenceDate) => {
    const refDate = referenceDate ? new Date(referenceDate) : null;
    const monthNumeric = Number(monthId);
    const yearNumeric = Number(year);
    const normalizedMonth = Number.isFinite(monthNumeric)
      ? monthNumeric - 1
      : refDate
        ? refDate.getMonth()
        : new Date().getMonth();
    const safeMonth = Math.min(Math.max(normalizedMonth, 0), 11);
    const safeYear = Number.isFinite(yearNumeric)
      ? yearNumeric
      : refDate
        ? refDate.getFullYear()
        : new Date().getFullYear();
    const start = new Date(safeYear, safeMonth, 1);
    const end = new Date(safeYear, safeMonth + 1, 0);
    const label = `Pagesa për periudhën ${formatPeriodDate(start)} - ${formatPeriodDate(end)}`;
    return {
      start,
      end,
      label,
      shortLabel: `${formatPeriodDate(start)} - ${formatPeriodDate(end)}`,
    };
  };

  const maskAccountNumber = (account) => {
    const clean = String(account || '').replace(/\s+/g, '');
    if (!clean) return '—';
    if (clean.length <= 4) return clean;
    return `********${clean.slice(-4)}`;
  };

  const buildWorkerContext = (workerInfo = {}, salary = {}) => {
    const fullName =
      workerInfo?.name ||
      `${workerInfo?.FirstName || ''} ${workerInfo?.LastName || ''}`.trim() ||
      t('worker.table.employee');
    const personalId =
      workerInfo?.PersonalId ||
      workerInfo?.IdNumber ||
      workerInfo?.IDNumber ||
      workerInfo?.PersonalNumber ||
      '—';
    const employeeNumber =
      workerInfo?.EmployeeNumber ||
      workerInfo?.EmployeeNo ||
      workerInfo?.EmployeeNr ||
      workerInfo?.EmployeeCode ||
      workerInfo?.Code ||
      workerInfo?.Id ||
      '—';
    const contractNumber =
      workerInfo?.ContractNumber ||
      workerInfo?.ContractNo ||
      workerInfo?.ContractNr ||
      workerInfo?.ContractCode ||
      salary?.ContractNumber ||
      '—';
    const organization =
      workerInfo?.Employer ||
      workerInfo?.CompanyName ||
      workerInfo?.Company ||
      workerInfo?.Organization ||
      '—';
    const institution =
      workerInfo?.Institution ||
      workerInfo?.Department ||
      workerInfo?.DepartmentName ||
      workerInfo?.Unit ||
      '—';
    const jobTitle =
      workerInfo?.Position ||
      workerInfo?.JobTitle ||
      workerInfo?.Profession ||
      workerInfo?.Title ||
      salary?.Position ||
      '—';
    const workplace =
      workerInfo?.WorkPlace ||
      workerInfo?.Location ||
      workerInfo?.City ||
      workerInfo?.Municipality ||
      '—';
    const classification =
      workerInfo?.Class ||
      workerInfo?.Classification ||
      workerInfo?.Grade ||
      workerInfo?.Category ||
      '—';
    const coefficient =
      workerInfo?.Coefficient ||
      workerInfo?.Coeficient ||
      workerInfo?.CoefficientValue ||
      salary?.Coefficient ||
      '—';
    const experience =
      workerInfo?.Experience ||
      workerInfo?.YearsOfService ||
      workerInfo?.ExperienceText ||
      workerInfo?.Seniority ||
      '—';
    const workHours =
      salary?.WorkHours ||
      workerInfo?.WorkingHours ||
      workerInfo?.WeeklyHours ||
      workerInfo?.Hours ||
      '—';
    const jobType = workerInfo?.EmploymentType || workerInfo?.ContractType || salary?.JobType || '—';
    const bankName = workerInfo?.BankName || workerInfo?.Bank || salary?.Bank || '—';
    const bankAccount = maskAccountNumber(
      workerInfo?.BankAccount ||
      workerInfo?.Iban ||
      workerInfo?.IBAN ||
      workerInfo?.AccountNumber ||
      salary?.AccountNumber ||
      salary?.BankAccount
    );

    return {
      fullName,
      personalId,
      employeeNumber,
      contractNumber,
      organization,
      institution,
      jobTitle,
      workplace,
      classification,
      coefficient,
      experience,
      workHours,
      jobType,
      bankName,
      bankAccount,
    };
  };

  const renderSalarySlipPdf = ({ workerName, monthName, year, salary, totals, workerInfo, period }) => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 12;
    const contentWidth = pageWidth - margin * 2;
    const workerDetails = buildWorkerContext(workerInfo, salary);
    const headerLabel = period?.label || `${monthName || 'Muaji'} ${year || ''}`.trim();

    doc.setFillColor(245, 247, 252);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');

    doc.setFillColor(212, 224, 245);
    doc.roundedRect(margin, 14, contentWidth, 18, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(23, 33, 56);
    doc.text(headerLabel, margin + 6, 26);

    let y = 38;
    const drawSection = (title, leftRows, rightRows) => {
      const maxRows = Math.max(leftRows.length, rightRows.length);
      const rowHeight = 10;
      const sectionHeight = 18 + maxRows * rowHeight;
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(224, 229, 242);
      doc.roundedRect(margin, y, contentWidth, sectionHeight, 4, 4, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(23, 33, 56);
      doc.text(title, margin + 6, y + 10);
      const columnWidth = (contentWidth - 24) / 2;

      const drawColumn = (rows, columnIndex) => {
        let innerY = y + 18;
        const startX = margin + 6 + columnIndex * (columnWidth + 12);
        rows.forEach(({ label, value }) => {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(7.5);
          doc.setTextColor(120, 124, 156);
          doc.text(String(label || '').toUpperCase(), startX, innerY);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(11);
          doc.setTextColor(23, 33, 56);
          const textLines = doc.splitTextToSize(String(value || '—'), columnWidth);
          doc.text(textLines, startX, innerY + 5);
          innerY += rowHeight;
        });
      };

      drawColumn(leftRows, 0);
      drawColumn(rightRows, 1);
      y += sectionHeight + 8;
    };

    drawSection(
      t('addList.pdfGeneralData'),
      [
        { label: t('addList.pdfEmployee'), value: workerDetails.fullName || workerName },
        { label: t('addList.pdfPersonalId'), value: workerDetails.personalId },
        { label: t('addList.pdfWorkNumber'), value: workerDetails.employeeNumber },
        { label: t('addList.pdfContractNumber'), value: workerDetails.contractNumber },
      ],
      [
        { label: t('addList.pdfWorkHours'), value: workerDetails.workHours },
        { label: t('addList.pdfContractType'), value: workerDetails.jobType },
        { label: t('addList.pdfBank'), value: workerDetails.bankName },
        { label: t('addList.pdfAccountNumber'), value: workerDetails.bankAccount },
      ]
    );

    drawSection(
      t('addList.pdfOrganization'),
      [
        { label: t('addList.pdfInstitution'), value: workerDetails.organization },
        { label: t('addList.pdfJobTitle'), value: workerDetails.jobTitle },
        { label: t('addList.pdfLocation'), value: workerDetails.workplace },
      ],
      [
        { label: t('addList.pdfClass'), value: workerDetails.classification },
        { label: t('addList.pdfCoefficient'), value: workerDetails.coefficient },
        { label: t('addList.pdfExperience'), value: workerDetails.experience },
      ]
    );

    const paymentSectionHeight = 68;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(224, 229, 242);
    doc.roundedRect(margin, y, contentWidth, paymentSectionHeight, 4, 4, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(23, 33, 56);
    doc.text(t('addList.pdfPaymentDetails'), margin + 6, y + 10);

    const detailColumnWidth = (contentWidth - 28) / 2;
    const detailRowHeight = 8;
    const drawDetailCard = (title, rows, columnIndex) => {
      const startX = margin + 6 + columnIndex * (detailColumnWidth + 16);
      let innerY = y + 18;
      doc.setFillColor(248, 249, 253);
      doc.roundedRect(startX, innerY, detailColumnWidth, paymentSectionHeight - 26, 3, 3, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(96, 104, 134);
      doc.text(title, startX + 4, innerY + 6);
      innerY += 12;
      rows.forEach(({ label, value }) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(120, 124, 156);
        doc.text(String(label || ''), startX + 4, innerY);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(23, 33, 56);
        doc.text(String(value || '—'), startX + detailColumnWidth - doc.getTextWidth(String(value || '—')) - 4, innerY);
        innerY += detailRowHeight;
      });
    };

    drawDetailCard(
      t('addList.pdfSalaryInfo'),
      [
        { label: t('salaries.salary'), value: formatCurrency(totals.baseSalary) },
        { label: t('salaries.extraHoursPay'), value: formatCurrency(totals.overtimePay) },
        { label: t('salaries.bonuses'), value: formatCurrency(totals.bonus) },
        { label: t('addList.pdfSeniority'), value: formatCurrency(salary?.SeniorityValue || 0) },
      ],
      0
    );

    drawDetailCard(
      t('addList.statDeductions'),
      [
        { label: t('salaries.contributions'), value: formatCurrency(totals.contributions) },
        { label: t('salaries.taxes'), value: formatCurrency(totals.taxes) },
        { label: t('salaries.advance'), value: formatCurrency(totals.advances) },
        { label: t('salaries.penalties'), value: formatCurrency(totals.penalties) },
        ...(totals.personalHealthIns > 0 ? [{ label: t('worker.healthInsPersonal', 'Sig. Shënd. Personal'), value: formatCurrency(totals.personalHealthIns) }] : []),
        ...(totals.familyHealthIns > 0   ? [{ label: t('worker.healthInsFamily',   'Sig. Shënd. Familjar'),  value: formatCurrency(totals.familyHealthIns) }] : []),
      ],
      1
    );

    y += paymentSectionHeight + 10;

    const drawSummaryBar = (label, value, fillColor, textColor) => {
      doc.setFillColor(...fillColor);
      doc.roundedRect(margin, y, contentWidth, 14, 3, 3, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(98, 102, 132);
      doc.text(label, margin + 6, y + 9);
      doc.setTextColor(...textColor);
      const valueText = String(value || '—');
      doc.text(valueText, margin + contentWidth - doc.getTextWidth(valueText) - 6, y + 9);
      y += 20;
    };

    drawSummaryBar(t('addList.statGross'), formatCurrency(totals.grossIncome), [251, 238, 224], [176, 92, 25]);
    drawSummaryBar(t('salaries.netSalary'), formatCurrency(totals.netPay), [232, 244, 238], [16, 104, 74]);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(97, 103, 133);
    doc.text(t('addList.pdfAutoGenerated'), margin, y + 4);
    doc.text(t('addList.emailBankNote'), margin, y + 10);

    return doc;
  };

  const buildSalaryPdfAttachment = ({ workerName, monthName, year, salary, totals, workerInfo, period }) => {
    const doc = renderSalarySlipPdf({ workerName, monthName, year, salary, totals, workerInfo, period });
    if (!doc) return null;
    const filename = buildSalaryPdfFileName(workerName, monthName, year);
    const base64 = doc.output('datauristring').split(',')[1];
    return {
      filename,
      name: filename,
      originalName: filename,
      content: base64,
      encoding: 'base64',
      contentType: 'application/pdf',
      mimeType: 'application/pdf',
    };
  };

  const notifyEmployeesAboutSalaries = async (listMeta, payloadItems) => {
    const actionableItems = (payloadItems || [])
      .map((item) => (item?.entity ? { ...item.entity, changeStatus: item.status } : item))
      .filter((item) => (item?.changeStatus ?? item?.status) !== STATUS.deleted);
    if (!actionableItems.length) {
      return;
    }

    const monthId = listMeta?.month || listMeta?.Month || watch('month')?.id;
    const monthName = months.find((m) => m.id === monthId)?.name;
    const yearValue = listMeta?.year || watch('year') || new Date().getFullYear();

    if (!notificationsConfigured) {
      setToast({
        open: true,
        message: t('addList.notificationsNotConfigured'),
        variant: 'info',
      });
      return;
    }

    const emails = actionableItems
      .map((item) => {
        const workerInfo = workerDirectory.find(
          (w) => String(w?.Id || w?.id) === String(item?.WorkerId)
        );
        const email = workerInfo?.Email || workerInfo?.email;
        if (!email) {
          return null;
        }
        const workerName =
          workerInfo?.name ||
          [workerInfo?.FirstName, workerInfo?.LastName].filter(Boolean).join(' ') ||
          t('worker.table.employee');
        const totals = buildSalaryTotals(item);
        const period = buildPayrollPeriod(monthId, yearValue, item?.SalaryDate);
        const pdfAttachment = buildSalaryPdfAttachment({
          workerName,
          monthName,
          year: yearValue,
          salary: item,
          totals,
          workerInfo,
          period,
        });
        if (!pdfAttachment) {
          return null;
        }

        const periodLabel = period?.shortLabel || `${monthName || t('common.month')} ${yearValue}`;
        const subject = t('addList.emailSubject', { period: periodLabel });
        const body = [
          t('addList.emailGreeting', { name: workerName }),
          '',
          t('addList.emailBody', { period: periodLabel, amount: formatCurrency(totals.netPay) }),
          t('addList.emailDetails'),
          t('addList.emailBankNote'),
          '',
          t('addList.emailSignature'),
          t('addList.emailDept'),
        ].join('\n');

        return {
          payload: {
            to: [email],
            subject,
            body,
            attachments: [pdfAttachment],
          },
        };
      })
      .filter(Boolean);

    if (!emails.length) {
      setToast({
        open: true,
        message: t('addList.noEmailFound'),
        variant: 'info',
      });
      return;
    }

    try {
      const results = await Promise.allSettled(
        emails.map(({ payload }) => notificationsService.sendEmail(payload))
      );
      const successCount = results.filter((r) => r.status === 'fulfilled').length;
      const failedCount = results.length - successCount;
      if (successCount) {
        setToast({
          open: true,
          message: t('addList.emailsSent', { count: successCount }),
          variant: 'success',
        });
      }
      if (failedCount) {
        setToast({
          open: true,
          message: t('addList.emailsFailed', { count: failedCount }),
          variant: 'warning',
        });
      }
    } catch (error) {
      setToast({
        open: true,
        message: t('addList.emailSendFailed'),
        variant: 'warning',
      });
    }
  };

  /**
   * Orët nga dialogu «Orët shtesë».
   * Merr të gjashtë kategoritë si objekt — më parë ishin tre argumente pozicionalë
   * dhe shtimi i natës apo kujdestarisë do të kërkonte ndryshim te të dy anët.
   */
  const setExtraHours = (hours, no, hourSalary) => {
    const newArr = [...salaries];
    const row = { ...newArr[no - 1], ...hours, status: STATUS.changed, HourSalary: parseFloat(hourSalary) || 0 };
    newArr[no - 1] = row;
    setSalaries(newArr);
    setValues(newArr);
    if (fiscalRef.current) {
      prepareEmployeeCard(fiscalRef.current, newArr);
    } else {
      getFiscalPolitics(newArr);
    }
  }

  /**
   * Eksporti i listës.
   *
   * Prodhon TË NJËJTIN regjistër si faqja e pagave — jo një tabelë të dytë me
   * kolona të tjera. Më parë kishte dy eksporte të së njëjtës pagë, dhe të dyja
   * do të ndryshonin nga njëra-tjetra ditën që dikush prekte njërën.
   *
   * Ndërtohet nga gjendja në ekran, jo nga baza: rreshtat mund të jenë ende të
   * paruajtur, dhe eksporti duhet të tregojë atë që sheh përdoruesi.
   */
  // ── Llogaritje nga neto ───────────────────────────────────────────────────
  // Paga te ekrani llogaritet bruto → neto. Por marrëveshja me punëtorin bëhet
  // shpesh mbi neton, dhe atëherë duhet e kundërta: cila pagë bazë e jep atë
  // neto, me këto breza e norma. Zgjidhet me kërkim binar te salaryCalc.
  const [netCalc, setNetCalc] = useState(null);   // { no, targetNet, preview }
  const openNetCalc = (row) => {
    const t = buildSalaryTotals(row);
    setNetCalc({ no: row.no, targetNet: String(t.netPay ? t.netPay.toFixed(2) : ''), preview: null });
  };
  const runNetCalc = (value) => {
    setNetCalc((c) => {
      if (!c) return c;
      const row = salaries.find((r) => r.no === c.no) || {};
      const params = paramsFromRow(row, fiscalPolicyRef.current || {}, fiscalRef.current || []);
      const preview = grossFromNet(parseFloat(value), params);
      return { ...c, targetNet: value, preview };
    });
  };
  const applyNetCalc = () => {
    if (!netCalc?.preview) return;
    const { no, preview } = netCalc;
    setValue(`Salary_${no}`, preview.base);
    setSalaries((prev) => {
      const updated = prev.map((r) => (r.no === no ? { ...r, Salary: preview.base, status: STATUS.changed } : r));
      if (fiscalRef.current) prepareEmployeeCard(fiscalRef.current, updated);
      else getFiscalPolitics(updated);
      return updated;
    });
    setNetCalc(null);
    setToast({ open: true, variant: 'success', message: `Paga bazë u vendos në ${preview.base.toFixed(2)} €` });
  };

  const [exporting, setExporting] = useState(false);
  const exportRegister = async (kind) => {
    const active = salaries.filter((r) => r.status !== -1 && r.WorkerId);
    if (!active.length) {
      setToast({ open: true, variant: 'warning', message: 'Lista nuk ka rreshta për eksport.' });
      return;
    }
    setExporting(true);
    try {
      const settings = await apiService().get('settings');
      const rows = active.map((r) => {
        const t = buildSalaryTotals(r);
        const w = workers.find((x) => getWorkerId(x) === r.WorkerId) || {};
        return {
          FirstName: w.FirstName || w.first_name || '',
          LastName:  w.LastName  || w.last_name  || '',
          NID: w.PersonalNumber || w.personal_number || '',
          SecondJob: (w.SecondJob ?? w.second_job) === true,
          Salary: t.baseSalary, HourSalary: t.overtimePay, Bonus: t.bonus,
          employeeTaxFee: t.contributions, employerTaxFee: t.employerContribution,
          HealthInsPersonal: t.personalHealthIns, HealthInsFamily: t.familyHealthIns,
          Advances: t.advances, Penalties: t.penalties,
          neto: t.netPay,
        };
      });
      const monthVal = watch('month');
      const reg = buildRegisterKS(rows, {
        list: { month: monthVal?.id ?? monthVal, year: Number(watch('year')) || undefined },
        company: settings?.data?.[0] || {},
        ranges: fiscalRef.current || [],
      });
      if (kind === 'excel')      exportRegisterExcel(reg);
      else if (kind === 'pdf')   await downloadRegisterPdf(reg);
      else if (kind === 'print' && !(await printRegister(reg))) {
        setToast({ open: true, variant: 'warning', message: 'Shfletuesi e bllokoi dritaren e printimit.' });
      }
    } catch (e) {
      setToast({ open: true, variant: 'error', message: e.message || 'Eksporti dështoi.' });
    } finally {
      setExporting(false);
    }
  };

  const history = useHistory();

  // Grand totals across all active rows
  const grandTotals = useMemo(() => {
    const active = salaries.filter(s => s.status !== -1 && s.WorkerId !== 0);
    return active.reduce((acc, w) => {
      const t = buildSalaryTotals(w);
      return {
        gross:   acc.gross   + t.grossIncome,
        contrib: acc.contrib + t.contributions,
        taxes:   acc.taxes   + t.taxes,
        net:     acc.net     + t.netPay,
        workers: acc.workers + 1,
      };
    }, { gross: 0, contrib: 0, taxes: 0, net: 0, workers: 0 });
  // buildSalaryTotals reads fiscalRef.current directly (no reactive dep needed)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salaries]);

  const activeRows = salaries.filter(s => s.status !== -1);

  return (
    <Box>
      <ExportData salaries={salaries} workers={workers} />
      <ExtraHours {...modal} handleClose={(id) => handleClose(id)} setHours={setExtraHours} />
      <Fragment>
        <Toast {...toast} />

        {/* ── Toolbar ──────────────────────────────────────────────────────
            Tri zona, të ndara me vija: kthimi · redaktimi · mbyllja.
            Më parë ishin shtatë butona të barabartë në një rresht, ku "Ruaj"
            dhe "Proceso" konkurronin si veprim kryesor. Tani ruajtja është e
            vetmja e mbushur majtas, procesimi rri i fundit — atje ku shkohet
            kur gjithçka tjetër ka mbaruar. */}
        <Paper elevation={0} sx={{ px: 1.5, py: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap
            sx={{ '& .MuiButton-root': { textTransform: 'none', borderRadius: 1.5, fontSize: '0.8rem', py: 0.5 } }}>

            <Tooltip title={t('common.back')}>
              <IconButton size="small" onClick={() => history.goBack()}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, width: 32, height: 32 }}>
                <ArrowBackIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>

            <MuiDivider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />

            <Button size="small" variant="contained" disableElevation startIcon={<SaveIcon sx={{ fontSize: 17 }} />}
              onClick={handleSubmit(onSubmit)} sx={{ fontWeight: 700 }}>
              {t('addList.saveList')}
            </Button>
            <Button size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: 17 }} />}
              onClick={addRow}>
              {t('addList.addRow')}
            </Button>
            {showAddAllWorkers() && (
              <Button size="small" variant="outlined" color="success" startIcon={<GroupAddIcon sx={{ fontSize: 17 }} />}
                onClick={addAllWorkers}>
                {t('addList.addAllWorkers')}
              </Button>
            )}

            <MuiDivider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />

            {/* Të dyja mbushin rreshtat nga një burim tjetër — një familje e
                vetme veprimesh, ndaj rrinë bashkë e jo si fqinjë të rastit. */}
            <ButtonGroup size="small" variant="outlined" color="secondary"
              sx={{ '& .MuiButton-root': { textTransform: 'none', fontSize: '0.8rem', py: 0.5 } }}>
              <Tooltip title={t('addList.prefillFromScheduleTooltip', 'Mbush automatikisht orët shtesë nga orari dhe prezenca')}>
                <span>
                  <Button startIcon={<AutoFixHighIcon sx={{ fontSize: 17 }} />}
                    onClick={prefillFromSchedule} disabled={prefilling}
                    sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}>
                    {prefilling ? t('common.loading', 'Duke ngarkuar…') : t('addList.prefillFromSchedule', 'Nga Orari')}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={t('addList.applyAdjustmentsTooltip', 'Bart shtesat, avancet dhe ndalesat e aprovuara të kësaj periudhe nga Rregullimet e pagës')}>
                <span>
                  <Button startIcon={<TuneIcon sx={{ fontSize: 17 }} />}
                    onClick={() => applyMonthAdjustments(false)} disabled={applyingAdj}
                    sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, ml: '-1px' }}>
                    {applyingAdj ? t('common.loading', 'Duke ngarkuar…') : t('addList.applyAdjustments', 'Nga Rregullimet')}
                  </Button>
                </span>
              </Tooltip>
            </ButtonGroup>

            <Box sx={{ flex: 1 }} />

            {/* Eksporti ishte buton teksti gri — lexohej si i çaktivizuar.
                Tani ndjek të njëjtin stil si eksportet e tjera të aplikacionit. */}
            <ButtonGroup size="small" variant="outlined"
              sx={{ '& .MuiButton-root': { textTransform: 'none', fontSize: '0.78rem', py: 0.45 } }}>
              <Tooltip title="Printo listën e pagave">
                <span>
                  <Button onClick={() => exportRegister('print')} disabled={exporting}
                    startIcon={<PrintIcon sx={{ fontSize: 16 }} />}>Printo</Button>
                </span>
              </Tooltip>
              <Tooltip title="Shkarko në Excel">
                <span>
                  <Button color="success" onClick={() => exportRegister('excel')} disabled={exporting}
                    startIcon={<GridOnIcon sx={{ fontSize: 16 }} />} sx={{ ml: '-1px' }}>Excel</Button>
                </span>
              </Tooltip>
              <Tooltip title="Shkarko në PDF">
                <span>
                  <Button color="error" onClick={() => exportRegister('pdf')} disabled={exporting}
                    startIcon={<PictureAsPdfIcon sx={{ fontSize: 16 }} />} sx={{ ml: '-1px' }}>PDF</Button>
                </span>
              </Tooltip>
            </ButtonGroup>

            {listId && canLock && (
              <>
                <MuiDivider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />
                {/* Mbetet i përdorshëm edhe pas procesimit: një korrigjim pas
                    tij duhet vetëm ruajtur e riprocesuar, jo rihapur diku tjetër.
                    Statusi tregohet me tekstin e butonit. */}
                <Tooltip title={isClosed
                  ? `Procesuar${lockedBy ? ` nga ${lockedBy}` : ''} — ruaj ndryshimet dhe riproceso`
                  : 'Ruan ndryshimet dhe e shënon listën si të procesuar'}>
                  <span>
                    <Button size="small" variant="contained" color="success" disableElevation
                      startIcon={<LockIcon sx={{ fontSize: 17 }} />}
                      disabled={processing}
                      onClick={() => setProcessOpen(true)}
                      sx={{ fontWeight: 700 }}>
                      {isClosed ? 'Riproceso' : 'Proceso pagat'}
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </Stack>
        </Paper>

        {/* ── Llogaritësi nga neto ── */}
        <Dialog open={Boolean(netCalc)} onClose={() => setNetCalc(null)} maxWidth="xs" fullWidth
          PaperProps={{ sx: { borderRadius: 2.5 } }}>
          <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem' }}>Llogarit nga neto</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} mt={0.5}>
              <Typography variant="body2" sx={{ color: '#6B7280' }}>
                Shkruaj neton e rënë dakord; sistemi gjen pagën bazë që e jep atë,
                me brezat dhe normat e politikës fiskale aktive.
              </Typography>
              <MuiTextField size="small" label="Neto e dëshiruar (€)" type="number" autoFocus
                inputProps={{ min: 0, step: 0.01 }}
                value={netCalc?.targetNet ?? ''}
                onChange={(e) => runNetCalc(e.target.value)} />

              {netCalc?.preview ? (
                <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                  {[
                    ['Paga bazë',            netCalc.preview.base,   true],
                    ['Bruto (me shtesat)',   netCalc.preview.gross],
                    ['Kontributi punëtori',  -netCalc.preview.contribEmployee],
                    ['Tatimi',               -netCalc.preview.tax],
                    ['Sigurimi',             -netCalc.preview.health],
                    ...(netCalc.preview.advances  ? [['Avanci',  -netCalc.preview.advances]]  : []),
                    ...(netCalc.preview.penalties ? [['Ndalesa', -netCalc.preview.penalties]] : []),
                    ['Neto',                 netCalc.preview.net,    true],
                  ].map(([k, v, strong]) => (
                    <Stack key={k} direction="row" justifyContent="space-between" sx={{ py: 0.25 }}>
                      <Typography variant="body2" sx={{ color: strong ? 'text.primary' : '#6B7280', fontWeight: strong ? 700 : 400 }}>{k}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: strong ? 800 : 600, color: v < 0 ? '#C2410C' : 'text.primary' }}>
                        {formatCurrency(Math.abs(v))}
                      </Typography>
                    </Stack>
                  ))}
                  <MuiDividerLine sx={{ my: 1 }} />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>Kosto e punëdhënësit</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>{formatCurrency(netCalc.preview.employerCost)}</Typography>
                  </Stack>
                </Box>
              ) : netCalc?.targetNet ? (
                <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
                  Kjo neto nuk arrihet me këto norma dhe ndalesa.
                </Alert>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1.5 }}>
            <Button onClick={() => setNetCalc(null)} sx={{ textTransform: 'none', fontWeight: 700 }}>Anulo</Button>
            <Button variant="contained" disableElevation onClick={applyNetCalc}
              disabled={!netCalc?.preview} sx={{ textTransform: 'none', fontWeight: 700 }}>
              Vendos pagën bazë
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Month / Year form + Summary ── */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                  {t('addList.listPeriod')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={7}>
                    <SelectController
                      placeholder={t('common.month')}
                      error={errors.month}
                      control={control}
                      options={months}
                      name="month"
                      register={{ ...register('month') }}
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <InputController
                      placeholder={t('addList.year')}
                      error={errors.year}
                      control={control}
                      type="number"
                      register={{ ...register('year') }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Grid container spacing={1.5} sx={{ height: '100%' }}>
              {[
                { label: t('addList.statWorkers'),  value: grandTotals.workers, color: '#3E8FA3', bg: '#EEF2FF', fmt: String },
                { label: t('addList.statGross'),     value: grandTotals.gross,   color: '#0891B2', bg: '#ECFEFF', fmt: formatCurrency },
                { label: t('addList.statDeductions'), value: grandTotals.contrib + grandTotals.taxes, color: '#D97706', bg: '#FFFBEB', fmt: formatCurrency },
                { label: t('addList.statNet'),        value: grandTotals.net,     color: '#3E8FA3', bg: '#ECFDF5', fmt: formatCurrency },
              ].map(s => (
                <Grid item xs={6} key={s.label}>
                  <Paper sx={{ p: '8px 12px', borderRadius: 2, bgcolor: s.bg, border: 'none', height: '100%' }}>
                    <Typography variant="caption" sx={{ color: s.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, display: 'block' }}>
                      {s.label}
                    </Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ color: '#101828', lineHeight: 1.2, mt: 0.25, fontSize: '1rem' }}>
                      {s.fmt(s.value)}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        {/* ── Workers table ── */}
        <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ p: '10px 14px', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2" fontWeight={700}>
                {t('addList.employeesList')}
                <Chip size="small" label={`${grandTotals.workers} ${t('addList.employeesChip')}`}
                  sx={{ ml: 1, bgcolor: '#EEF2FF', color: '#3E8FA3', fontWeight: 600, fontSize: '0.7rem' }} />
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('addList.clickRowToEdit')}
              </Typography>
            </Stack>
          </Box>
          <TableContainer>
            <Table size="small" id="content_table">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>{t('worker.table.employee')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('salaries.salary')}</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>{t('salaries.advance')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('salaries.extraHours')}</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>{t('addList.hourPay')}</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', lg: 'table-cell' } }}>{t('salaries.bonus')}</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', lg: 'table-cell' } }}>{t('addList.penaltiesShort')}</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>{t('salaries.taxes')}</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', sm: 'table-cell' } }}>{t('addList.contribShort')}</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', sm: 'table-cell' } }}>Sig. Shënd.</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#3E8FA3' }}>{t('salaries.netSalary')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeRows.map((w, index) => {
                  const rowTotals = buildSalaryTotals(w);
                  const workerName = getWorkerName(workers.find(i => getWorkerId(i) === w.WorkerId));
                  return (
                    <TableRow hover key={w.id + '-' + index}
                      sx={{ bgcolor: w.editMode ? 'primary.50' : 'inherit', '&:last-child td': { border: 0 } }}>

                      {/* Punëtori */}
                      <TableCell sx={{ py: 1 }}>
                        {w.editMode ? (
                          <SelectController
                            onChangeProp={() => onValueChange(w.no)}
                            salary={(e) => salary(e, w.no)}
                            error={errors.WorkerId}
                            control={control}
                            options={workers}
                            name={`WorkerId_${w.no}`}
                            value={workers.find(i => getWorkerId(i) === w.WorkerId) || null}
                            register={{ ...register('WorkerId_' + w.no) }}
                          />
                        ) : (
                          <Box onClick={() => onRowClick(w.no)} sx={{ cursor: 'pointer' }}>
                            <Typography variant="body2" fontWeight={600}>
                              {workerName || <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>{t('addList.selectWorkerPlaceholder')}</Box>}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>

                      {/* Paga bazë */}
                      <TableCell>
                        {w.editMode ? (
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <InputController onFieldChange={() => onValueChange(w.no)} error={errors['Salary_' + w.no]}
                              control={control} symbol="€" register={{ ...register('Salary_' + w.no) }} />
                            {/* Drejtimi i kundërt: nga neto e rënë dakord te paga bazë. */}
                            <Tooltip title="Llogarit nga neto">
                              <span>
                                <IconButton size="small" onClick={() => openNetCalc(w)} disabled={!w.WorkerId}
                                  sx={{ width: 26, height: 26 }}>
                                  <CalculateIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        ) : (
                          <Typography variant="body2" fontWeight={600} onClick={() => onRowClick(w.no)} sx={{ cursor: 'pointer' }}>
                            {formatCurrency(w.Salary)}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Avanci */}
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {w.editMode ? (
                          <InputController onFieldChange={() => onValueChange(w.no)} error={errors['Advances_' + w.no]}
                            control={control} symbol="€" register={{ ...register('Advances_' + w.no) }} />
                        ) : (
                          <Typography variant="body2" color={w.Advances > 0 ? 'warning.main' : 'text.secondary'}
                            onClick={() => onRowClick(w.no)} sx={{ cursor: 'pointer' }}>
                            {formatCurrency(w.Advances)}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Orë shtesë */}
                      <TableCell>
                        <Tooltip title={t('salaries.extraHours')}>
                          <Button size="small" variant="outlined" startIcon={<AccessTimeIcon />}
                            onClick={() => openModal(w.WorkerId, w.no, w, true)}
                            sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 70 }}>
                            {rowTotals.totalHours > 0 ? `${rowTotals.totalHours}h` : '—'}
                          </Button>
                        </Tooltip>
                      </TableCell>

                      {/* Pag. orë */}
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {w.editMode ? (
                          <InputController onFieldChange={() => onValueChange(w.no)} error={errors['HourSalary_' + w.no]}
                            control={control} symbol="€" register={{ ...register('HourSalary_' + w.no) }} />
                        ) : (
                          <Typography variant="body2" color="text.secondary" onClick={() => onRowClick(w.no)} sx={{ cursor: 'pointer' }}>
                            {formatCurrency(w.HourSalary)}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Bonus */}
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        {w.editMode ? (
                          <InputController onFieldChange={() => onValueChange(w.no)} error={errors['Bonus_' + w.no]}
                            control={control} symbol="€" register={{ ...register('Bonus_' + w.no) }} />
                        ) : (
                          <Typography variant="body2" color={w.Bonus > 0 ? 'success.main' : 'text.secondary'}
                            onClick={() => onRowClick(w.no)} sx={{ cursor: 'pointer' }}>
                            {formatCurrency(w.Bonus)}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Ndëshkime */}
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        {w.editMode ? (
                          <InputController onFieldChange={() => onValueChange(w.no)} error={errors['Penalties_' + w.no]}
                            control={control} symbol="€" type="number" register={{ ...register('Penalties_' + w.no) }} />
                        ) : (
                          <Typography variant="body2" color={w.Penalties > 0 ? 'error.main' : 'text.secondary'}
                            onClick={() => onRowClick(w.no)} sx={{ cursor: 'pointer' }}>
                            {formatCurrency(w.Penalties)}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Tatim */}
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary" onClick={() => onRowClick(w.no)} sx={{ cursor: 'pointer' }}>
                          {formatCurrency(rowTotals.taxes)}
                        </Typography>
                      </TableCell>

                      {/* Sigurimi punëtorit (pension) */}
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2" color="error.main" onClick={() => onRowClick(w.no)} sx={{ cursor: 'pointer' }}>
                          -{formatCurrency(rowTotals.contributions)}
                        </Typography>
                      </TableCell>

                      {/* Sigurimi shëndetësor */}
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2" color="error.main" onClick={() => onRowClick(w.no)} sx={{ cursor: 'pointer' }}>
                          -{formatCurrency(w.HealthInsEmployee || 0)}
                        </Typography>
                      </TableCell>

                      {/* Neto */}
                      <TableCell>
                        <Typography variant="body2" fontWeight={700} color="success.main">
                          {formatCurrency(rowTotals.netPay)}
                        </Typography>
                      </TableCell>

                      {/* Veprime */}
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                          <Tooltip title={t('addList.deleteRow')}>
                            <IconButton size="small" color="error" onClick={() => deleteRow(w.no)}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          {activeRows.length === index + 1 && (
                            <Tooltip title="Shto rresht">
                              <IconButton size="small" color="primary" onClick={addRow}>
                                <AddIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {activeRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                      <Alert severity="info" sx={{ display: 'inline-flex' }}>
                        {t('addList.noWorkersAlert', { button1: t('addList.addAllWorkers'), button2: t('addList.addRow') })}
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Grand totals footer */}
          {grandTotals.workers > 0 && (
            <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
              <Stack direction="row" spacing={3} flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">
                  {t('addList.total')}: <b style={{ color: '#101828' }}>{grandTotals.workers} {t('addList.employeesChip')}</b>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('addList.statGross')}: <b style={{ color: '#0891B2' }}>{formatCurrency(grandTotals.gross)}</b>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('addList.statDeductions')}: <b style={{ color: '#D97706' }}>{formatCurrency(grandTotals.contrib + grandTotals.taxes)}</b>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('addList.statNet')}: <b style={{ color: '#3E8FA3' }}>{formatCurrency(grandTotals.net)}</b>
                </Typography>
              </Stack>
            </Box>
          )}
        </Card>


        {/* Konfirmim — mbyllja e një muaji pagash nuk bëhet me një klikim të vetëm. */}
        <Dialog open={processOpen} onClose={processing ? undefined : () => setProcessOpen(false)}
          maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
          <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem' }}>
            {isClosed ? 'Riproceso pagat' : 'Proceso pagat'}
          </DialogTitle>
          <DialogContent dividers>
            <Typography sx={{ fontSize: '0.88rem', color: 'text.primary', mb: 1 }}>
              Ndryshimet ruhen dhe lista shënohet si e procesuar.
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
              Rregullimet e aprovuara të kësaj periudhe lidhen me këtë listë dhe nuk
              hyjnë dy herë. Lista mbetet e ndryshueshme — çdo ndryshim pas procesimit
              shënohet te historiku me kohë dhe përdorues.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1.5 }}>
            <Button onClick={() => setProcessOpen(false)} disabled={processing}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              Anulo
            </Button>
            <Button variant="contained" color="success" disableElevation
              onClick={processPayroll} disabled={processing}
              startIcon={processing ? <CircularProgress size={15} color="inherit" /> : <LockIcon />}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              {processing ? 'Duke procesuar…' : 'Proceso dhe mbyll'}
            </Button>
          </DialogActions>
        </Dialog>

      </Fragment>
    </Box>
  );
}
